import { useEffect, useMemo, useState } from 'react';
import { useCan } from '../useCan';
import { kassaDayFor } from '../../core/rules/staffAccess';
import { CashDayCloses } from './CashDayCloses';
import { toast } from 'sonner';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { LoadFailed } from '../ui/LoadState';
import { Skeleton } from '../../ui/Skeleton';
import { btnPrimary, btnSecondary, fadeWhileLoading } from '../ui/styles';
import { dayLong, shiftDate, today } from '../../core/format/dates';
import { useKassa } from './useKassa';
import { SaleComposer } from './SaleComposer';
import { ToSettleList } from './ToSettleList';
import { SaleList } from './SaleList';
import { DaySummary } from './DaySummary';
import type { KassaAppointment } from '../../core/types/kassa';

/** Wat er open staat: niets, een losse bon, of een bon voor een afspraak. */
type Composing = {
  appointment: KassaAppointment | null;
} | null;

/**
 * De kassa: afrekenen in de salon. Links wat er af te rekenen is en de bonnen van de dag, rechts het
 * dagoverzicht met de afsluiting. Een bon maken vervangt de linkerkolom; het overzicht blijft staan.
 */
export function Kassa() {
  const k = useKassa();
  const can = useCan();
  const seesTotals = can.canAll('kassa.totals');
  const canRefund = can.canAll('kassa.refund');
  const {
    staffId
  } = can;
  const [composing, setComposing] = useState<Composing>(null);
  // Een collega ziet alleen haar eigen afspraken en bonnen, zonder dagtotalen (core/rules/staffAccess).
  const data = useMemo(() => k.data && k.data.day === k.day ? kassaDayFor(k.data, {
    isOwner: seesTotals,
    staffId
  }) : null, [k.data, k.day, seesTotals, staffId]);
  const editable = Boolean(data?.editable);

  // Vanuit de agenda (?afspraak=): meteen de bon van die afspraak openen, zodra de dag geladen is.
  const {
    appointmentId,
    clearAppointment
  } = k;
  useEffect(() => {
    if (!appointmentId || !data) return;
    const a = data.toSettle.find(x => x.id === appointmentId);
    if (a && data.editable) setComposing({
      appointment: a
    });else toast.info(data.editable ? 'Deze afspraak is al afgerekend.' : 'Deze dag neemt geen bonnen meer aan.');
    clearAppointment();
  }, [appointmentId, data, clearAppointment]);

  // Andere dag: een half ingevulde bon hoort bij vandaag, niet bij die dag.
  useEffect(() => setComposing(null), [k.day]);
  const dayNav = <>
      {/* De hoofdhandeling van de kassa: altijd bovenaan, niet als klein knopje in een lijst. */}
      {editable && <button type="button" onClick={() => setComposing({
      appointment: null
    })} className={btnPrimary} data-id="element-2287" data-mp="2caomve29we48">
          <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2288" data-mp="2q60iros60amq" />
          Losse bon
        </button>}
      <button type="button" onClick={() => k.setDay(shiftDate(k.day, -1))} className={btnSecondary} aria-label="Vorige dag" data-id="element-2289" data-mp="3023spgt9ddk8">
        <ChevronLeftIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2290" data-mp="2v4bt00mdgn5h" />
      </button>
      {!k.isToday && <button type="button" onClick={() => k.setDay(shiftDate(k.day, 1))} className={btnSecondary} aria-label="Volgende dag" data-id="element-2291" data-mp="3epkg07av8k6c">
          <ChevronRightIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2292" data-mp="3ioba05k1blp8" />
        </button>}
      {!k.isToday && <button type="button" onClick={() => k.setDay(today())} className={btnSecondary} data-id="element-2293" data-mp="39p6e76zuy6gr">
          Vandaag
        </button>}
    </>;
  return <div data-id="element-2294" data-mp="2uz8lqeuwaulr">
      <PageHeader title="Kassa" description={`${k.isToday ? 'Vandaag, ' : ''}${dayLong(k.day)}`} actions={composing ? undefined : dayNav} data-id="element-2295" data-mp="2zrry58hujjoz" />

      {k.status === 'error' && !data ? <LoadFailed title="De kassa laden lukte niet" text="Controleer je verbinding. Er is niets verloren: bonnen staan op de server." onRetry={k.reload} data-id="element-2296" data-mp="2lyai3xodcp3p" /> : !data ? <div role="status" aria-label="Kassa laden" className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]" data-id="element-2297" data-mp="2h1ak8a1oasno">
          <Skeleton className="h-64 w-full rounded-lg" data-id="element-2298" data-mp="3kby5ilmfyndq" />
          <Skeleton className="h-64 w-full rounded-lg" data-id="element-2299" data-mp="3kbelmzd8dox2" />
        </div> : <div className={`grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] ${fadeWhileLoading(k.status === 'loading')}`} data-id="element-2300" data-mp="3feftgmd4ns1i">
          <div className="min-w-0 space-y-8" data-id="element-2301" data-mp="3akbp8av8xy44">
            {composing ? <SaleComposer appointment={composing.appointment} onCancel={() => setComposing(null)} onDone={() => {
          setComposing(null);
          k.refresh();
        }} data-id="element-2302" data-mp="35ms72zvfh16w" /> : <>
                {!editable && <p className="rounded-md bg-sand px-4 py-3 text-[13px] text-ink" data-id="element-2303" data-mp="2mofo6mepscy4">
                    {data.closed ? 'Deze dag is afgesloten. Nieuwe bonnen of correcties kunnen niet meer.' : 'Een eerdere dag. Afrekenen kan alleen vandaag.'}
                    {canRefund && ' Komt er iets terug? Open de bon en kies Retour: dat telt vandaag.'}
                  </p>}
                <section aria-labelledby="kassa-open" data-id="element-2304" data-mp="sera6zc7qb9m">
                  <h2 id="kassa-open" className="mb-3 text-[15px] font-medium text-ink" data-id="element-2305" data-mp="xen60barawe3">
                    Af te rekenen
                  </h2>
                  <ToSettleList items={data.toSettle} editable={editable} onSettle={a => setComposing({
              appointment: a
            })} data-id="element-2306" data-mp="128rc6sc770e4" />
                </section>
                <section aria-labelledby="kassa-bonnen" data-id="element-2307" data-mp="9isq95tavjdo">
                  <h2 id="kassa-bonnen" className="mb-3 text-[15px] font-medium text-ink" data-id="element-2308" data-mp="egudm2xre4uy">
                    Bonnen
                  </h2>
                  <SaleList sales={data.sales} editable={editable} canReturn={canRefund} onVoided={k.refresh} data-id="element-2309" data-mp="jevxgq30e87h" />
                </section>
              </>}
          </div>
          <aside className="space-y-6 lg:sticky lg:top-6" data-id="element-2310" data-mp="1uvvkb66l7uu4">
            <DaySummary data={data} totalsHidden={data.totalsHidden} onClosed={() => k.refresh()} data-id="element-2311" data-mp="1uxf0yxw5o7wf" />
            {seesTotals && <CashDayCloses version={data.closed?.closedAt ?? 'open'} onOpenDay={k.setDay} data-id="element-2312" data-mp="3vxbnx37gsxpj" />}
          </aside>
        </div>}
    </div>;
}